/**
 * Wheel Share Video — Advanced Customize model (C5, SPEC §5.3).
 *
 * The deterministic, framework-free core the Customize panel renders around:
 * which Segments are included, in what ORDER, and the TIMING (a global per-Segment
 * duration → a total that must respect the chosen platform's cap). No React, no
 * network, no DOM — so the selection/order/timing math is exhaustively unit-
 * testable and the panel stays a thin presentational shell (same split as
 * ./shareVideo, ./presets, ./capabilities).
 *
 * Selecting Customize OVERRIDES the preset (SPEC §5.3): it emits an explicit
 * ORDERED `segment_ids` array + a clamped `target_duration_ms`, and NO
 * `preset` / `segment_count` — the explicit list IS the selection.
 *
 * DELIBERATELY SELF-CONTAINED — imports nothing from ./shareVideo, so
 * shareVideo.buildRequestPayload can import `customizeToParams` from here with no
 * import cycle. The aspect keys below mirror ./shareVideo ASPECT_OPTIONS values.
 *
 * BE PARAM GAP (measured 2026-08-15, BE sandbox): the generate-share-video[-async]
 * view reads ONLY `aspect_ratio` and ignores extra keys; the service layer
 * (`generate_share_video`) already accepts a `segment_ids` kwarg but the view
 * never passes it, and there is no duration param. Honoring an explicit ordered
 * selection + duration is a follow-on BE unit — the FE sends them honestly rather
 * than faking a segment set it cannot resolve from `wheelId` alone.
 */

// Per-aspect HARD duration cap (ms), a FE mirror of the BE C3 platform cap
// (SPEC §4: 9:16 hard ~60s; 1:1 / 4:5 / 16:9 target 6–30s). The BE re-enforces
// this in config validation; this mirror only drives the live over-cap warning +
// the client-side clamp so the emitted target_duration_ms is never out of range.
export const PLATFORM_DURATION_CAP_MS = Object.freeze({
  "9:16": 60000,
  "1:1": 30000,
  "4:5": 30000,
  "16:9": 30000,
});

// Fallback cap for an unknown aspect (defensive — the picker only offers the
// four above). The floor mirrors SPEC §4's "6–…s" minimum for every platform.
export const DEFAULT_DURATION_CAP_MS = 30000;
export const MIN_TOTAL_DURATION_MS = 6000;

// Global per-Segment reveal duration (SPEC §5.3 "per-Segment (or global) …
// sliders"). v1 uses one global slider; the total = count × this.
export const DEFAULT_PER_SEGMENT_MS = 2500;
export const MIN_PER_SEGMENT_MS = 1000;
export const MAX_PER_SEGMENT_MS = 5000;

/** The hard duration cap (ms) for a platform aspect; unknown → default. */
export const capForAspect = (aspect) =>
  PLATFORM_DURATION_CAP_MS[aspect] || DEFAULT_DURATION_CAP_MS;

/**
 * Build the initial customize list from the Wheel's Segments. Every Segment is
 * INCLUDED by default, in its original order (SPEC §5.3: the checklist defaults
 * to all, with drag-to-reorder). Each item keeps id + display fields (title,
 * color/fill) for the row; unknown fields degrade to safe defaults so a partial
 * segment shape never throws.
 */
export function initSegmentList(segments = []) {
  return (Array.isArray(segments) ? segments : []).map((s) => ({
    id: s.id,
    title: s.title ?? "",
    color: s.color ?? null,
    included: true,
  }));
}

/** Toggle one Segment's included flag (by id); all other rows untouched. */
export const toggleSegment = (list, id) =>
  list.map((it) => (it.id === id ? { ...it, included: !it.included } : it));

/**
 * Move the item at `index` one slot up (dir = -1) or down (dir = +1). An
 * out-of-range move — up on the first row, down on the last, or a bad index — is
 * a no-op that returns the ORIGINAL list (reference-equal), so a UI can cheaply
 * detect "nothing moved". A valid move returns a new array.
 */
export function moveSegment(list, index, dir) {
  const target = index + dir;
  if (index < 0 || index >= list.length) return list;
  if (target < 0 || target >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

/** The included Segments' ids, IN CURRENT ORDER (this is the emitted selection). */
export const selectedSegmentIds = (list) =>
  (list || []).filter((it) => it.included).map((it) => it.id);

/** How many Segments are included. */
export const selectedCount = (list) =>
  (list || []).filter((it) => it.included).length;

/** At least one Segment included (the guard against submitting an empty reveal). */
export const hasSelection = (list) => selectedCount(list) > 0;

/**
 * Total reveal duration (ms) = (#included Segments) × per-Segment ms. A small,
 * global-per-Segment model for v1 (SPEC §5.3 allows "per-Segment (or global)").
 */
export const totalDurationMs = (list, perSegmentMs = DEFAULT_PER_SEGMENT_MS) =>
  selectedCount(list) * Math.max(0, Number(perSegmentMs) || 0);

/** True when a requested total exceeds the platform's hard cap (drives the warning). */
export const isOverCap = (totalMs, aspect) =>
  Number(totalMs) > capForAspect(aspect);

/**
 * Clamp a requested total to the platform cap AND the platform floor, so the
 * emitted `target_duration_ms` is always a valid, in-range value even when the
 * user's slider × count would blow past the cap (the UI still warns; the payload
 * never carries an illegal duration).
 */
export function clampDurationMs(totalMs, aspect) {
  const cap = capForAspect(aspect);
  const t = Number(totalMs) || 0;
  if (t > cap) return cap;
  if (t < MIN_TOTAL_DURATION_MS) return MIN_TOTAL_DURATION_MS;
  return t;
}

/**
 * The render params an explicit Customize selection contributes to the POST body
 * (SPEC §5.3). Returns:
 *   { segment_ids: [ordered ids], target_duration_ms: <clamped total> }
 * with NO `preset` / `segment_count` — the explicit list overrides the preset.
 *
 * Returns null when nothing is selected: the component guards the empty case
 * (Create disabled), and buildRequestPayload treats null as "fall back to the
 * preset path" so a defensive null can never emit a bogus empty selection.
 */
export function customizeToParams({ list, perSegmentMs, aspect } = {}) {
  const ids = selectedSegmentIds(list);
  if (ids.length === 0) return null;
  const total = totalDurationMs(list, perSegmentMs);
  return {
    segment_ids: ids,
    target_duration_ms: clampDurationMs(total, aspect),
  };
}
